import { FC, ReactNode, useEffect, useState } from "react"
import { useAuth } from "../../hooks/use-firebase"
import { SubscriptionTypeEnum } from "../../lib/enums"
import Navbar from "./navbar"
import Header from "./header"
import { useIsMobile } from "../../hooks/use-mobile"
import SubscriptionBanner from "../subscription/SubscriptionBanner"
import { useLocation } from "react-router-dom"
import SubscriptionOnboardingDialog from "../subscription/SubscriptionOnboardingDialog"
import { useUserContext } from "../../hooks/use-user"



type PageProps = {
	children: React.ReactNode
	title?: string | ReactNode
	hideNavbar?: boolean
}


const Page: FC<PageProps> = ({
	children,
	title,
	hideNavbar = false
}) => {

	const { currentUser } = useAuth()
	const { subscription } = useUserContext() // Use subscription from context instead of local state
	const [showRequiredOnboarding, setShowRequiredOnboarding] = useState(false)
	const isMobile = useIsMobile()
	const location = useLocation()

	// Pages that require premium subscription
	const premiumPages = ['/listings']
	const isPremiumPage = premiumPages.some(path => location.pathname.startsWith(path))

	// Check if we should show required onboarding modal
	useEffect(() => {
		// Don't show on subscription success/cancelled pages
		const isSubscriptionPage = location.pathname.includes('/subscription/')

		// Show modal for Free tier users (no subscription) on each page navigation
		// User can close it to explore, but it reappears on next page
		if (currentUser && subscription === SubscriptionTypeEnum.Free && !isSubscriptionPage) {
			// Small delay to let the page load
			const timer = setTimeout(() => {
				setShowRequiredOnboarding(true)
			}, 1000)
			return () => clearTimeout(timer)
		} else {
			// Hide modal if user has a subscription
			setShowRequiredOnboarding(false)
		}
	}, [currentUser, subscription, location.pathname]) // Re-show on route change

	const handleOnboardingClose = () => {
		// Allow closing for Free tier users to explore UI
		// Dialog will reappear when they navigate to a new page
		setShowRequiredOnboarding(false)
	}

	return (
		<div className="h-screen w-screen bg-neutral50 dark:bg-neutral900 text-neutral900 dark:text-neutral100 flex flex-col">
			{/* Required Subscription Onboarding Modal - Always shown for Free tier users until they subscribe */}
			<SubscriptionOnboardingDialog
				open={showRequiredOnboarding}
				onOpenChange={handleOnboardingClose}
			/>

			{/* Modern Header */}
			<Header subscription={subscription} />

			{/* Subscription Banner - shown on premium pages when user is on Basic tier */}

			{/* Main Content Area */}
			<div className={`flex w-full h-full ${isMobile ? 'pt-16 flex-col-reverse' : 'pt-16'}`}>
				{!hideNavbar && <Navbar />}
				<div className="flex-grow flex flex-col min-h-0">
					{/* Hide page title on mobile - bottom nav already shows current page */}
					{title && !isMobile && (
						<div className="bg-white dark:bg-neutral800 border-b border-neutral200 dark:border-neutral700 shadow-soft px-6 py-4">
							<div className="text-2xl font-bold text-neutral900 dark:text-neutral100 flex items-center gap-2">
								{title}
							</div>
						</div>
					)}
					<div className={`flex-grow overflow-y-auto bg-neutral50 dark:bg-neutral900 ${isMobile ? 'pb-20' : ''}`}>{children}</div>
				</div>
			</div>
		</div>
	)
}

export default Page
